import type { ReactNode } from "react";
import { Calendar, MapPin, Users } from "lucide-react";
import { getTypeConfig, formatAgeRange, formatDateRange, stripAdventureMeta } from "@/lib/adventure-helpers";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import AdventureHero from "@/components/adventures/AdventureHero";
import { isAdventureFull } from "../../../shared/activities";

interface AdventureCardProps {
  adventure: {
    id: string;
    type: string;
    title: string;
    description?: string | null;
    date_start: string;
    date_end: string | null;
    image_url?: string | null;
    location: string;
    min_child_age: number | null;
    max_child_age: number | null;
    organiser_name?: string;
    interested_count?: number;
    going_count?: number;
    max_group_size?: number;
  };
  onClick: () => void;
  action?: ReactNode;
}

const AdventureCard = ({ adventure, onClick, action }: AdventureCardProps) => {
  const typeConfig = getTypeConfig(adventure.type);
  const isFull = isAdventureFull(adventure.max_group_size, {
    interested: adventure.interested_count ?? 0,
    going: adventure.going_count ?? 0,
    waitlist: 0,
  });
  const footerSummary =
    (adventure.interested_count ?? 0) > 0 || (adventure.going_count ?? 0) > 0
      ? `${adventure.interested_count ?? 0} interested · ${adventure.going_count ?? 0} going${isFull ? " · Full · waitlist open" : ""}`
      : isFull
        ? "Full · waitlist open"
        : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className="kindred-content-card w-full cursor-pointer overflow-hidden rounded-card text-left transition-shadow hover:shadow-md"
    >
      <AdventureHero
        imageUrl={adventure.image_url}
        storageBucket="adventure-photos"
        badgeLabel={`${typeConfig.emoji} ${typeConfig.label}`}
        className="flex h-40 items-end p-4"
      />

      <div className="space-y-2 p-4">
        <div className="flex items-start gap-3">
          <h3 className="min-w-0 flex-1 text-base font-semibold text-foreground">{adventure.title}</h3>
          {action ? (
            <div
              className="shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {action}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Calendar size={14} className="shrink-0" />
          {formatDateRange(adventure.date_start, adventure.date_end)}
        </div>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin size={14} className="shrink-0" />
          {adventure.location}
        </div>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Users size={14} className="shrink-0" />
          {formatAgeRange(adventure.min_child_age, adventure.max_child_age)}
        </div>

        {"description" in adventure && stripAdventureMeta((adventure as { description?: string | null }).description) ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {stripAdventureMeta((adventure as { description?: string | null }).description)}
          </p>
        ) : null}

        {/* Footer */}
        <div className="flex items-center gap-2 pt-2">
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
              {(adventure.organiser_name ?? "K")[0]}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground">
            {adventure.organiser_name ?? "Kindred member"}
          </span>
          {footerSummary ? (
            <span className="ml-auto text-xs text-muted-foreground">
              {footerSummary}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdventureCard;
